import React, { AriaAttributes, ReactNode, forwardRef, useCallback, useMemo } from 'react';
import { Choice, ForwardedChoiceProps } from '../Choice';
import { Input } from './styled';
import { generateId } from '../../utils/generateId';

type RadioValue = string | number;

export interface RadioProps extends ForwardedChoiceProps, Pick<AriaAttributes, 'aria-label'> {
  id?: string;
  className?: string;
  checked: boolean;
  value?: RadioValue;
  disabled?: boolean;
  name?: string;
  onChange: (checked: boolean, value?: RadioValue) => void;
  children?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      checked,
      value,
      name,
      hint,
      disabled,
      onChange,
      id,
      children,
      'aria-label': ariaLabel,
      ...otherProps
    }: RadioProps,
    ref,
  ) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.currentTarget.checked, value);
      },
      [onChange, value],
    );

    const componentId = useMemo(() => id ?? generateId('radio'), [id]);
    const ariaDescribedBy = hint ? `hint-${componentId}` : undefined;

    return (
      <Choice htmlFor={componentId} disabled={disabled} hint={hint} {...otherProps}>
        <Input
          id={componentId}
          type="radio"
          checked={checked}
          value={value}
          name={name}
          disabled={disabled}
          onChange={handleChange}
          ref={ref}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
        />
        {children}
      </Choice>
    );
  },
);
